/**
 * Created by neo on 09.12.2024
 * Individual Challenge Participant - Tracks user participation in an individual challenge
 */
import { makeAutoObservable } from 'mobx';
import { Audited, createAuditedFields } from '../../Audited';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { RouteChallengeLocationPosition, createRouteChallengeLocationPosition } from '../RouteChallenge/RouteChallengeLocationPosition';
import { Pageable } from '../../Interfaces/Pageable';
import { Media, createMedia } from '../../Media/Media';
import { RouteChallengeTeamReachedLocation, createRouteChallengeTeamReachedLocation } from '../RouteChallenge/RouteChallengeTeamReachedLocation';
import { RouteChallengeLocation, createRouteChallengeLocation } from '../RouteChallenge/RouteChallengeLocation';

export type IndividualChallengeParticipantRequest = Pageable & {
  query?: string;
  gymId?: string;
  challengeId?: string;
  latitude?: number;
  longitude?: number;
  maxDistance?: number;
};

export type IndividualChallengeRankingRequest = Pageable & {};

export type IndividualChallengeParticipant = Audited & {
  challengeId: string;
  athleteId: string;
  customerId: string;
  gymId?: string;
  joinedAt: Date;
  leftAt?: Date;
  active: boolean;
  challengeStarted: boolean;
  challengeEnded: boolean;
  challengeStartDateTime?: Date;
  challengeEndDateTime?: Date;
  challengeCompletedDateTime?: Date;
  totalCollectedPoints: number;
  extraCollectedPoints?: number | null;
  todayCollectedPoints: number;
  lastPointsCollectedDate?: Date;
  currentLocationIndex: number;
  reachedLocations: RouteChallengeTeamReachedLocation[];
  currentLocation?: RouteChallengeLocation;
  nextLocation?: RouteChallengeLocation;
  rank?: number;
  _reachedLocationsCount?: number;
  currentPosition: RouteChallengeLocationPosition;
  profilePicture?: Media;
  nickname?: string;
  readonly coordinates: { latitude: number; longitude: number };
  readonly hasCompletedChallenge: boolean;
  readonly reachedLocationsCount: number;
  join(): Promise<void>;
  leave(): Promise<void>;
};

export function createIndividualChallengeParticipant(json?: Partial<IndividualChallengeParticipant>): IndividualChallengeParticipant {
  const audited = createAuditedFields(json);
  return makeAutoObservable({
    ...audited,
    challengeId: json?.challengeId ?? '',
    athleteId: json?.athleteId ?? '',
    customerId: json?.customerId ?? '',
    gymId: json?.gymId,
    joinedAt: json?.joinedAt ? new Date(json.joinedAt as any) : new Date(),
    leftAt: json?.leftAt ? new Date(json.leftAt as any) : undefined,
    active: json?.active ?? true,
    challengeStarted: json?.challengeStarted ?? false,
    challengeEnded: json?.challengeEnded ?? false,
    challengeStartDateTime: json?.challengeStartDateTime ? new Date(json.challengeStartDateTime as any) : undefined,
    challengeEndDateTime: json?.challengeEndDateTime ? new Date(json.challengeEndDateTime as any) : undefined,
    challengeCompletedDateTime: json?.challengeCompletedDateTime ? new Date(json.challengeCompletedDateTime as any) : undefined,
    totalCollectedPoints: json?.totalCollectedPoints ?? 0,
    extraCollectedPoints: json?.extraCollectedPoints ?? null,
    todayCollectedPoints: json?.todayCollectedPoints ?? 0,
    lastPointsCollectedDate: json?.lastPointsCollectedDate ? new Date(json.lastPointsCollectedDate as any) : undefined,
    currentLocationIndex: json?.currentLocationIndex ?? 0,
    reachedLocations: ((json?.reachedLocations as any) ?? []).map((l: any) => createRouteChallengeTeamReachedLocation(l)),
    _reachedLocationsCount: (json as any)?.reachedLocationsCount,
    currentLocation: json?.currentLocation ? createRouteChallengeLocation(json.currentLocation as any) : undefined,
    nextLocation: json?.nextLocation ? createRouteChallengeLocation(json.nextLocation as any) : undefined,
    rank: json?.rank,
    currentPosition: createRouteChallengeLocationPosition(json?.currentPosition as any),
    profilePicture: json?.profilePicture ? createMedia(json.profilePicture as any) : undefined,
    nickname: json?.nickname,

    get coordinates(): { latitude: number; longitude: number } {
      return { longitude: this.currentPosition.yorLng, latitude: this.currentPosition.xorLat };
    },

    get hasCompletedChallenge(): boolean {
      return !!this.challengeCompletedDateTime;
    },

    get reachedLocationsCount(): number {
      return this._reachedLocationsCount ?? Math.max(0, this.reachedLocations.length - 1);
    },

    join(): Promise<void> {
      return HttpBackend.post(`/coach-program/engagement/individual-challenges/${this.challengeId}/join`);
    },

    leave(): Promise<void> {
      return HttpBackend.post(`/coach-program/engagement/individual-challenges/${this.challengeId}/leave`);
    },
  });
}

export const IndividualChallengeParticipantApi = {
  myParticipation(challengeId: string): Promise<IndividualChallengeParticipant | undefined> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${challengeId}/my-participation`)
      .then((response) => (response ? createIndividualChallengeParticipant(response) : undefined))
      .catch((error) => {
        if (error?.response?.status === 404 || error?.status === 404) {
          return undefined;
        }
        console.warn('Error fetching participation:', error);
        return undefined;
      });
  },

  join(challengeId: string): Promise<void> {
    return HttpBackend.post(`/coach-program/engagement/individual-challenges/${challengeId}/join`);
  },

  leave(challengeId: string): Promise<void> {
    return HttpBackend.post(`/coach-program/engagement/individual-challenges/${challengeId}/leave`);
  },

  find(request?: Partial<IndividualChallengeParticipantRequest>): Promise<IndividualChallengeParticipant[]> {
    if (!request?.challengeId) {
      return Promise.resolve([]);
    }

    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${request.challengeId}/participants`, {
      latitude: request.latitude,
      longitude: request.longitude,
      maxDistanceKm: request.maxDistance ? request.maxDistance / 1000 : undefined,
      page: request.page,
      size: request.size,
    })
      .then((response) => (response ?? []).map((r: any) => createIndividualChallengeParticipant(r)))
      .catch(() => IndividualChallengeParticipantApi.ranking(request.challengeId!, { page: 0, size: request.size ?? 50 }));
  },

  count(challengeId: string): Promise<number> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${challengeId}/participants`, { size: 0 })
      .then((response) => response?.length ?? 0)
      .catch(() => 0);
  },

  ranking(challengeId: string, request?: Partial<IndividualChallengeRankingRequest>): Promise<IndividualChallengeParticipant[]> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${challengeId}/ranking/participants`, {
      ...request,
    })
      .then((response) => (response ?? []).map((r: any) => createIndividualChallengeParticipant(r)))
      .catch(() => []);
  },

  rankingCount(challengeId: string): Promise<number> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${challengeId}/ranking/count`)
      .then((response) => response ?? 0)
      .catch(() => 0);
  },
};
